import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogContent, DialogTitle, Divider, IconButton, Paper, Tab, Tabs,
    ThemeProvider, ToggleButton, ToggleButtonGroup, Typography, createTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import ContrastIcon from '@mui/icons-material/Contrast';
import CampaignIcon from '@mui/icons-material/Campaign';
import LockIcon from '@mui/icons-material/Lock';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PlaceIcon from '@mui/icons-material/Place';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import dayjs from 'dayjs';
import ApplicationAppService from '../../../../application/platform/application-app-service';
import MenuAppService from '../../../../application/platform/apm/menu/menu-app-service';
import BannerAppService from '../../../../application/platform/apm/banner/banner-app-service';
import { TIPO_SECAO_HOME_LABEL, TipoSecaoHome } from '../../../../model/apm/apm-enums';
import { IHomeSectionResponse } from '../../../../model/apm/home/response/home-section-response';
import { IHomeSectionItemResponse } from '../../../../model/apm/home/response/home-section-item-response';
import { IBannerResponse } from '../../../../model/apm/banner/response/banner-response';
import { AcessoRapidoIcon } from '../../components/apm-icons';
import { buildImageUrl } from '../../components/image-utils';
import { sanitizeHtml } from '../../components/html-utils';
import { topCarouselBanners, visibleSections } from './home-preview-utils';
import { PREVIEW_BANNERS, PREVIEW_NEWS, sampleImage } from './home-preview-samples';
import { formatDate } from '../../../../utils/date';
import { parseGalleryConfiguration } from './home-section-config';

type Device = 'desktop' | 'mobile';

interface HomePreviewDialogProps {
    open: boolean;
    sections: IHomeSectionResponse[];
    isEnabled: boolean;
    onClose: () => void;
}

const FALLBACK_MENUS = ['O Município', 'Notícias', 'Diário Oficial', 'Serviços', 'Transparência', 'Fale Conosco'];

// Mesmo visual do portal público (docged.portal.municipio): esquema azul, Nunito, cards arredondados
const portalTheme = createTheme({
    palette: {
        primary: { main: '#1976d2', dark: '#0d47a1', light: '#42a5f5', contrastText: '#ffffff' },
        background: { default: '#f5f5f5', paper: '#ffffff' },
        text: { primary: '#212121', secondary: '#757575' },
    },
    typography: {
        fontFamily: '"Nunito", "Roboto", "Helvetica", "Arial", sans-serif',
        button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: { borderRadius: 8 },
});

const cardSx = {
    borderRadius: '12px',
    overflow: 'hidden',
    bgcolor: 'background.paper',
    boxShadow: '0 2px 8px rgba(0,0,0,.08)',
    transition: 'all .2s ease-in-out',
    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,.12)' },
};

// ---------- Blocos ----------

function SectionTitle({ title, subtitle, mobile }: { title: string; subtitle?: string; mobile: boolean }) {
    return (
        <Box sx={{ mb: 3 }}>
            <Typography component="h2" sx={{ fontSize: mobile ? '1.5rem' : '2rem', fontWeight: 700, color: 'primary.main' }}>{title}</Typography>
            {subtitle && <Typography variant="body1" color="text.secondary">{subtitle}</Typography>}
        </Box>
    );
}

function Columns({ cols, children }: { cols: number; children: React.ReactNode }) {
    return <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>{children}</Box>;
}

function SampleChip() {
    return <Chip size="small" label="Exemplo" sx={{ position: 'absolute', top: 12, left: 12, zIndex: 2, bgcolor: 'rgba(0,0,0,.6)', color: '#fff' }} />;
}

// ---------- Seções ----------

interface Slide {
    title: string;
    image: string;
}

function BannerCarousel({ slides, usingSamples, mobile }: { slides: Slide[]; usingSamples: boolean; mobile: boolean }) {
    const [current, setCurrent] = useState(0);
    const index = current % slides.length;

    useEffect(() => {
        if (slides.length < 2) return;
        const timer = setInterval(() => setCurrent((c) => (c + 1) % slides.length), 5000);
        return () => clearInterval(timer);
    }, [slides.length]);

    const arrowSx = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,.8)', '&:hover': { bgcolor: '#fff' }, zIndex: 2 };

    return (
        <Box sx={{ position: 'relative', width: '100%', height: mobile ? 220 : 420, borderRadius: '16px', overflow: 'hidden', boxShadow: 3, bgcolor: 'grey.300' }}>
            {usingSamples && <SampleChip />}
            {slides.map((slide, i) => (
                <Box key={i} sx={{ position: 'absolute', inset: 0, opacity: i === index ? 1 : 0, transition: 'opacity .8s ease-in-out' }}>
                    <Box component="img" src={slide.image} alt={slide.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    {slide.title && (
                        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: 3, pb: 5, background: 'linear-gradient(to top, rgba(0,0,0,.7), transparent)' }}>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: mobile ? '1.1rem' : '2rem', textShadow: '0 2px 4px rgba(0,0,0,.5)' }}>
                                {slide.title}
                            </Typography>
                        </Box>
                    )}
                </Box>
            ))}
            {slides.length > 1 && (
                <React.Fragment>
                    <IconButton size={mobile ? 'small' : 'medium'} sx={{ ...arrowSx, left: 16 }} onClick={() => setCurrent((index - 1 + slides.length) % slides.length)}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <IconButton size={mobile ? 'small' : 'medium'} sx={{ ...arrowSx, right: 16 }} onClick={() => setCurrent((index + 1) % slides.length)}>
                        <ChevronRightIcon />
                    </IconButton>
                    <Box sx={{ position: 'absolute', bottom: 16, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 1, zIndex: 2 }}>
                        {slides.map((_, i) => (
                            <Box key={i} onClick={() => setCurrent(i)} sx={{ width: 10, height: 10, borderRadius: '50%', cursor: 'pointer', bgcolor: i === index ? '#fff' : 'rgba(255,255,255,.5)' }} />
                        ))}
                    </Box>
                </React.Fragment>
            )}
        </Box>
    );
}

// Carrossel do topo: vem da tela de Banners e não faz parte da customização (fica sempre entre o header e as seções)
function TopBannerCarousel({ banners, mobile }: { banners: IBannerResponse[]; mobile: boolean }) {
    const usingSamples = banners.length === 0;
    const slides = usingSamples
        ? PREVIEW_BANNERS
        : banners.map((banner, index) => ({ title: banner.title || '', image: buildImageUrl(banner.image) || sampleImage(index, 'banner') }));
    return <BannerCarousel slides={slides} usingSamples={usingSamples} mobile={mobile} />;
}

// Seção Banners: banner de corpo, na posição escolhida; sem banner vigente o portal não exibe a seção.
// Mesmo layout dos portais (BodyBanner): card com imagem ao lado do painel na cor do portal, diferente do carrossel do topo
function BodyBannerSection({ section, mobile }: { section: IHomeSectionResponse; mobile: boolean }) {
    const items = section.items || [];
    const [current, setCurrent] = useState(0);
    const total = items.length;

    useEffect(() => {
        if (total < 2) return;
        const timer = setInterval(() => setCurrent((c) => (c + 1) % total), 6000);
        return () => clearInterval(timer);
    }, [total]);

    if (total === 0) return <EmptyHint text="Nenhum banner vigente: a seção não aparece no portal." />;

    const index = current % total;
    const item = items[index];
    const navSx = { color: 'inherit', border: '1px solid rgba(255,255,255,.5)' };

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '3fr 2fr', height: mobile ? 'auto' : 240, borderRadius: '12px', overflow: 'hidden', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Box component="img" src={buildImageUrl(item.image) || sampleImage(index, 'banner')} alt={item.title} sx={{ width: '100%', height: mobile ? 160 : '100%', objectFit: 'cover', display: 'block' }} />
            <Box sx={{ bgcolor: 'primary.main', color: '#fff', p: mobile ? 2.5 : 3, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, minWidth: 0 }}>
                {item.title && (
                    <Typography sx={{ fontSize: mobile ? '1.1rem' : '1.35rem', fontWeight: 700, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {item.title}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {item.url && (
                        <Button variant="contained" endIcon={<ArrowForwardIcon />} sx={{ bgcolor: '#fff', color: 'primary.main', '&:hover': { bgcolor: 'rgba(255,255,255,.9)' } }}>
                            Saiba mais
                        </Button>
                    )}
                    {total > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                            <IconButton size="small" sx={navSx} onClick={() => setCurrent((index - 1 + total) % total)}><ChevronLeftIcon fontSize="small" /></IconButton>
                            <Typography variant="body2" sx={{ minWidth: 36, textAlign: 'center' }}>{index + 1} / {total}</Typography>
                            <IconButton size="small" sx={navSx} onClick={() => setCurrent((index + 1) % total)}><ChevronRightIcon fontSize="small" /></IconButton>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

function NewsSection({ section, mobile }: { section: IHomeSectionResponse; mobile: boolean }) {
    const quantity = section.itemsQuantity ?? 4;
    const news = Array.from({ length: Math.min(quantity, 8) }).map((_, i) => PREVIEW_NEWS[i % PREVIEW_NEWS.length]);
    return (
        <Box>
            <SectionTitle title={section.title || 'Últimas Notícias'} subtitle={section.subtitle} mobile={mobile} />
            <Columns cols={mobile ? 1 : 4}>
                {news.map((n, i) => (
                    <Box key={i} sx={{ ...cardSx, position: 'relative', display: 'flex', flexDirection: 'column' }}>
                        {i === 0 && <SampleChip />}
                        <Box component="img" src={n.image} alt={n.title} sx={{ width: '100%', height: mobile ? 180 : 200, objectFit: 'cover' }} />
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                <CalendarTodayIcon sx={{ fontSize: 14 }} />
                                <Typography variant="caption">{formatDate(n.date)}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {n.title}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, textTransform: 'uppercase' }}>Leia mais →</Typography>
                        </Box>
                    </Box>
                ))}
            </Columns>
            <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button variant="outlined" endIcon={<ArrowForwardIcon />} sx={{ borderWidth: 2, '&:hover': { borderWidth: 2 } }}>Ver todas as notícias</Button>
            </Box>
            {quantity > news.length && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    A seção exibe as {quantity} notícias mais recentes
                </Typography>
            )}
        </Box>
    );
}

function DiarioOficialSection({ section, mobile }: { section: IHomeSectionResponse; mobile: boolean }) {
    const year = dayjs().year();
    const years = [{ year, quantity: 128 }, { year: year - 1, quantity: 312 }, { year: year - 2, quantity: 297 }];
    return (
        <Box>
            <SectionTitle
                title={section.title || 'Diário Oficial e Publicações'}
                subtitle={section.subtitle || 'Acesse as edições do Diário Oficial e as publicações do município'}
                mobile={mobile}
            />
            <Paper elevation={2} sx={{ p: mobile ? 2 : 3, borderRadius: '12px' }}>
                <Tabs value={0} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label="Diário Oficial" />
                    <Tab label="Publicações" />
                </Tabs>
                {years.map((y) => (
                    <Box key={y.year} sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography fontWeight={600}>Diário Oficial - {y.year}</Typography>
                            <Typography variant="caption" color="text.secondary">{y.quantity} publicações</Typography>
                        </Box>
                        <ChevronRightIcon color="primary" />
                    </Box>
                ))}
                <Button fullWidth variant="contained" sx={{ mt: 2 }}>Ver todos os Diários Oficiais</Button>
            </Paper>
        </Box>
    );
}

function TextoLivreSection({ section, mobile }: { section: IHomeSectionResponse; mobile: boolean }) {
    const html = useMemo(() => sanitizeHtml(section.content), [section.content]);
    return (
        <Box>
            {section.title && <SectionTitle title={section.title} subtitle={section.subtitle} mobile={mobile} />}
            <Box
                sx={{ fontSize: '1rem', lineHeight: 1.7, '& h2': { fontSize: '1.5rem', color: 'primary.main' }, '& h3': { fontSize: '1.2rem' }, '& a': { color: 'primary.main' } }}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </Box>
    );
}

function AcessoRapidoSection({ section, mobile }: { section: IHomeSectionResponse; mobile: boolean }) {
    const items = section.items || [];
    return (
        <Box>
            <SectionTitle title={section.title || 'Acesso Rápido'} subtitle={section.subtitle} mobile={mobile} />
            {items.length === 0 ? <EmptyHint text="Nenhum atalho cadastrado." /> : (
                <Columns cols={mobile ? 2 : 5}>
                    {items.map((item) => (
                        <Box
                            key={item.id}
                            sx={{
                                ...cardSx, p: 2.5, textAlign: 'center',
                                '&:hover .qa-circle': { bgcolor: 'primary.main', color: '#fff' },
                            }}
                        >
                            <Box
                                className="qa-circle"
                                sx={{ width: 80, height: 80, mx: 'auto', mb: 1.5, borderRadius: '50%', bgcolor: 'primary.light', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .2s', '& svg': { fontSize: '2rem' } }}
                            >
                                <AcessoRapidoIcon name={item.icon} fontSize="inherit" />
                            </Box>
                            <Typography fontWeight={600}>{item.title}</Typography>
                            {item.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{item.description}</Typography>}
                        </Box>
                    ))}
                </Columns>
            )}
        </Box>
    );
}

function CardDestaqueSection({ section, mobile }: { section: IHomeSectionResponse; mobile: boolean }) {
    const items = section.items || [];
    return (
        <Box>
            <SectionTitle title={section.title || 'Destaques'} subtitle={section.subtitle} mobile={mobile} />
            {items.length === 0 ? <EmptyHint text="Nenhum card cadastrado." /> : (
                <Columns cols={mobile ? 1 : Math.min(items.length, 3)}>
                    {items.map((item, i) => (
                        <Box key={item.id} sx={cardSx}>
                            <Box component="img" src={buildImageUrl(item.image) || sampleImage(i)} alt={item.title} sx={{ width: '100%', height: 200, objectFit: 'cover' }} />
                            <Box sx={{ p: 2 }}>
                                <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>{item.title}</Typography>
                                {item.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{item.description}</Typography>}
                                {item.url && <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'primary.main', fontWeight: 600, textTransform: 'uppercase' }}>Saiba mais →</Typography>}
                            </Box>
                        </Box>
                    ))}
                </Columns>
            )}
        </Box>
    );
}

function VideosSection({ section, mobile }: { section: IHomeSectionResponse; mobile: boolean }) {
    const items = section.items || [];
    return (
        <Box>
            <SectionTitle title={section.title || 'Vídeos'} subtitle={section.subtitle} mobile={mobile} />
            {items.length === 0 ? <EmptyHint text="Nenhum vídeo cadastrado." /> : (
                <Columns cols={mobile ? 1 : Math.min(items.length, 3)}>
                    {items.map((item) => (
                        <Box key={item.id} sx={cardSx}>
                            <Box sx={{ aspectRatio: '16 / 9', bgcolor: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PlayCircleIcon sx={{ fontSize: 56, color: '#ff0000' }} />
                            </Box>
                            {item.title && <Typography fontWeight={600} sx={{ p: 2 }}>{item.title}</Typography>}
                        </Box>
                    ))}
                </Columns>
            )}
        </Box>
    );
}

function GaleriaImagensSection({ section, mobile }: { section: IHomeSectionResponse; mobile: boolean }) {
    const items = (section.items || []).filter((item) => !!item.image);
    const { layout, columns } = parseGalleryConfiguration(section.configuration);
    const cols = mobile ? Math.min(columns, 2) : columns;
    const tile = (item: IHomeSectionItemResponse, i: number) => (
        <Box key={item.id} component="figure" sx={{ m: 0, minWidth: 0 }}>
            <Box sx={{ ...cardSx, aspectRatio: '4 / 3' }}>
                <Box component="img" src={buildImageUrl(item.image) || sampleImage(i)} alt={item.title || ''} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </Box>
            {item.title && <Typography component="figcaption" variant="body2" color="text.secondary" noWrap sx={{ mt: 1 }}>{item.title}</Typography>}
        </Box>
    );

    return (
        <Box>
            <SectionTitle title={section.title || 'Galeria de imagens'} subtitle={section.subtitle} mobile={mobile} />
            {items.length === 0 ? <EmptyHint text="Nenhuma imagem cadastrada." /> : layout === 'carousel' ? (
                <Box sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'grid', gap: 2, gridAutoFlow: 'column', gridAutoColumns: `calc((100% - ${(cols - 1) * 16}px) / ${cols})`, overflow: 'hidden' }}>
                        {items.map(tile)}
                    </Box>
                    {items.length > cols && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: 2 }}>
                            {Array.from({ length: Math.ceil(items.length / cols) }).map((_, i) => (
                                <Box key={i} sx={{ width: i === 0 ? 20 : 8, height: 8, borderRadius: 4, bgcolor: i === 0 ? 'primary.main' : 'grey.300' }} />
                            ))}
                        </Box>
                    )}
                </Box>
            ) : (
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>{items.map(tile)}</Box>
            )}
        </Box>
    );
}

function EmptyHint({ text }: { text: string }) {
    return (
        <Box sx={{ p: 3, border: '1px dashed', borderColor: 'divider', borderRadius: '12px', textAlign: 'center', color: 'text.secondary', bgcolor: 'background.paper' }}>
            <Typography variant="body2">{text}</Typography>
        </Box>
    );
}

function SectionPreview({ section, mobile }: { section: IHomeSectionResponse; mobile: boolean }) {
    switch (section.type) {
        case TipoSecaoHome.Banners: return <BodyBannerSection section={section} mobile={mobile} />;
        case TipoSecaoHome.DiarioOficial: return <DiarioOficialSection section={section} mobile={mobile} />;
        case TipoSecaoHome.Noticias: return <NewsSection section={section} mobile={mobile} />;
        case TipoSecaoHome.TextoLivre: return <TextoLivreSection section={section} mobile={mobile} />;
        case TipoSecaoHome.AcessoRapido: return <AcessoRapidoSection section={section} mobile={mobile} />;
        case TipoSecaoHome.CardDestaque: return <CardDestaqueSection section={section} mobile={mobile} />;
        case TipoSecaoHome.Videos: return <VideosSection section={section} mobile={mobile} />;
        case TipoSecaoHome.GaleriaImagens: return <GaleriaImagensSection section={section} mobile={mobile} />;
        default: return null;
    }
}

// ---------- Estrutura do portal ----------

function PortalHeader({ entityName, menus, mobile }: { entityName: string; menus: string[]; mobile: boolean }) {
    const topItemSx = { display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' };
    return (
        <Box>
            {/* Barra superior */}
            <Box sx={{ bgcolor: 'primary.dark', color: '#fff', minHeight: 40, px: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={topItemSx}><AccessibilityNewIcon sx={{ fontSize: 18 }} />{!mobile && 'Acessibilidade'}</Box>
                {!mobile && (
                    <Box sx={{ display: 'flex', gap: 1, opacity: 0.9 }}>
                        <TextDecreaseIcon sx={{ fontSize: 18 }} /><TextIncreaseIcon sx={{ fontSize: 18 }} /><ContrastIcon sx={{ fontSize: 18 }} />
                    </Box>
                )}
                <Box sx={{ flex: 1 }} />
                {!mobile && <Box sx={topItemSx}><CampaignIcon sx={{ fontSize: 18 }} />Ouvidoria</Box>}
                <Box sx={topItemSx}><LockIcon sx={{ fontSize: 18 }} />{!mobile && 'Área Administrativa'}</Box>
                <SearchIcon sx={{ fontSize: 20 }} />
            </Box>
            {/* Marca */}
            <Box sx={{ bgcolor: 'background.paper', py: mobile ? 3 : 4, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: mobile ? 60 : 80, height: mobile ? 60 : 80, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AccountBalanceIcon sx={{ fontSize: mobile ? 32 : 42 }} />
                </Box>
                <Typography component="h1" sx={{ fontSize: mobile ? '1.25rem' : '2rem', fontWeight: 700, textAlign: 'center', px: 2 }}>{entityName}</Typography>
            </Box>
            {/* Menu */}
            <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', boxShadow: '0 2px 4px rgba(0,0,0,.05)' }}>
                {mobile ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
                        <Typography fontWeight={700} color="primary.main">MENU</Typography>
                        <MenuIcon />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5, py: 1, px: 2 }}>
                        {['Início', ...menus].map((m, i) => (
                            <Box
                                key={`${m}-${i}`}
                                sx={{
                                    px: 2, py: 1, borderRadius: 1, fontSize: '0.875rem', fontWeight: 600, textTransform: 'uppercase',
                                    bgcolor: i === 0 ? 'primary.main' : 'transparent', color: i === 0 ? '#fff' : 'text.primary',
                                }}
                            >
                                {m}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function PortalFooter({ entityName, mobile }: { entityName: string; mobile: boolean }) {
    const contactSx = { display: 'flex', alignItems: 'center', gap: 1, mb: 1, '& svg': { color: 'primary.main', fontSize: 18 } };
    return (
        <Box sx={{ bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', pt: 5, pb: 3, px: mobile ? 2 : 4 }}>
            <Columns cols={mobile ? 1 : 3}>
                <Box>
                    <Typography fontWeight={700}>{entityName}</Typography>
                    <Typography variant="body2" color="text.secondary">CNPJ: 00.000.000/0001-00</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button size="small" variant="outlined">e-OUV</Button>
                        <Button size="small" variant="outlined">e-SIC</Button>
                    </Box>
                </Box>
                <Box>
                    <Typography fontWeight={700} sx={{ mb: 1 }}>Navegar pelo site</Typography>
                    {['Início', 'A Cidade', 'Notícias', 'Diário Oficial', 'Licitações', 'Contato'].map((l) => (
                        <Typography key={l} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{l}</Typography>
                    ))}
                </Box>
                <Box>
                    <Typography fontWeight={700} sx={{ mb: 1 }}>Gabinete do Prefeito</Typography>
                    <Box sx={contactSx}><PhoneIcon /><Typography variant="body2">(00) 0000-0000</Typography></Box>
                    <Box sx={contactSx}><EmailIcon /><Typography variant="body2">contato@municipio.gov.br</Typography></Box>
                    <Box sx={contactSx}><PlaceIcon /><Typography variant="body2">Praça Principal, s/n - Centro</Typography></Box>
                    <Box sx={contactSx}><AccessTimeIcon /><Typography variant="body2">Segunda a Sexta: 8h às 17h</Typography></Box>
                </Box>
            </Columns>
            <Divider sx={{ my: 3 }} />
            <Typography variant="body2" color="text.secondary" align="center">© {dayjs().year()} - {entityName} - Todos os direitos reservados</Typography>
        </Box>
    );
}

// ---------- Dialog ----------

export function HomePreviewDialog({ open, sections, isEnabled, onClose }: HomePreviewDialogProps) {
    const [device, setDevice] = useState<Device>('desktop');
    const [menus, setMenus] = useState<string[]>(FALLBACK_MENUS);
    const [banners, setBanners] = useState<IBannerResponse[]>([]);
    const mobile = device === 'mobile';
    const entityName = ApplicationAppService.getAuthenticationData()?.company?.name || 'Prefeitura Municipal';
    const today = dayjs().format('YYYY-MM-DD');
    const visible = useMemo(() => visibleSections(sections, today), [sections, today]);
    const topBanners = useMemo(() => topCarouselBanners(banners, today), [banners, today]);
    const hiddenCount = sections.length - visible.length;

    useEffect(() => {
        if (!open) return;
        MenuAppService.list()
            .then((result) => {
                const titles = result.filter((m) => m.isVisible).map((m) => m.title || '');
                setMenus(titles.length > 0 ? titles : FALLBACK_MENUS);
            })
            .catch(() => setMenus(FALLBACK_MENUS));
        BannerAppService.list()
            .then(setBanners)
            .catch(() => setBanners([]));
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { height: '92vh' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" noWrap>Pré-visualização da home</Typography>
                    <Typography variant="caption" color="text.secondary" component="div" noWrap>
                        Simulação no layout do portal: header, carrossel do topo e footer fixos; abaixo, as seções visíveis na ordem atual. Notícias usam textos fictícios.
                    </Typography>
                </Box>
                <ToggleButtonGroup exclusive size="small" value={device} onChange={(_, next: Device | null) => next && setDevice(next)}>
                    <ToggleButton value="desktop" aria-label="Desktop"><DesktopWindowsIcon fontSize="small" /></ToggleButton>
                    <ToggleButton value="mobile" aria-label="Celular"><PhoneIphoneIcon fontSize="small" /></ToggleButton>
                </ToggleButtonGroup>
                <IconButton onClick={onClose} aria-label="Fechar"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: 'grey.300', p: { xs: 1, md: 3 } }}>
                {(!isEnabled || hiddenCount > 0) && (
                    <Box sx={{ maxWidth: mobile ? 390 : 1280, mx: 'auto', mt: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {!isEnabled && <Alert severity="warning">A home customizada está inativa: hoje o portal exibe a home padrão. Esta prévia mostra como ficará após ativá-la.</Alert>}
                        {hiddenCount > 0 && <Alert severity="info">{hiddenCount} seção(ões) oculta(s) não aparece(m) na prévia.</Alert>}
                    </Box>
                )}

                <ThemeProvider theme={portalTheme}>
                    <Box
                        sx={{
                            maxWidth: mobile ? 390 : 1280, mx: 'auto', bgcolor: 'background.default', fontFamily: portalTheme.typography.fontFamily,
                            borderRadius: mobile ? 4 : 1, boxShadow: 6, overflow: 'hidden', border: mobile ? '10px solid #222' : 'none',
                        }}
                    >
                        <PortalHeader entityName={entityName} menus={menus} mobile={mobile} />

                        <Box sx={{ p: mobile ? 2 : 4, display: 'flex', flexDirection: 'column', gap: mobile ? 4 : 6 }}>
                            {/* Header + carrossel do topo e footer são do portal: a customização só ordena o corpo */}
                            <Box sx={{ position: 'relative' }}>
                                <Chip
                                    size="small"
                                    label="Carrossel do topo · tela de Banners"
                                    sx={{ position: 'absolute', top: -12, right: 0, zIndex: 3, bgcolor: 'grey.600', color: '#fff', height: 20, fontSize: '0.7rem', opacity: 0.85 }}
                                />
                                <TopBannerCarousel banners={topBanners} mobile={mobile} />
                            </Box>

                            {visible.length === 0 && <EmptyHint text="Nenhuma seção visível na home." />}
                            {visible.map((section) => (
                                <Box key={section.id} sx={{ position: 'relative' }}>
                                    <Chip
                                        size="small"
                                        label={TIPO_SECAO_HOME_LABEL[section.type]}
                                        sx={{ position: 'absolute', top: -12, right: 0, zIndex: 3, bgcolor: 'grey.800', color: '#fff', height: 20, fontSize: '0.7rem', opacity: 0.85 }}
                                    />
                                    <SectionPreview section={section} mobile={mobile} />
                                </Box>
                            ))}
                        </Box>

                        <PortalFooter entityName={entityName} mobile={mobile} />
                    </Box>
                </ThemeProvider>
            </DialogContent>
        </Dialog>
    );
}
